<template>
  <div class="w-full">
    <!-- 博客卡片列表 -->
    <div class="blog-list-container">
      <transition-group
        name="blog-card"
        tag="div"
        class="space-y-6 sm:space-y-8"
        @before-enter="onBeforeEnter"
        @enter="onEnter"
      >
        <BlogPostCard
          v-for="(post, index) in paginatedPosts"
          :key="post.id"
          :post="post"
          :index="index"
          :data-index="index"
        />
      </transition-group>
    </div>

    <!-- 粒子容器 -->
    <div ref="particleContainer" class="particle-container"></div>

    <!-- 分页组件 -->
    <Pagination
      v-if="posts.length > pageSize"
      v-model="currentPage"
      :total="posts.length"
      :page-size="pageSize"
      @change="handlePageChange"
    />

    <!-- 加载动画 -->
    <LoadingAnimation :show="isLoading" loading-text="加载中" />
  </div>
</template>

<script>
import { ref, computed, nextTick, onMounted, onBeforeUnmount } from "vue";
import BlogPostCard from "./BlogPostCard.vue";
import Pagination from "./Pagination.vue";
import LoadingAnimation from "./LoadingAnimation.vue";
import { useBlogPosts } from "@/composables/useBlogPosts";
import { savePageState, getPageState } from "@/utils/navigationState";

export default {
  name: "BlogPostList",
  components: {
    BlogPostCard,
    Pagination,
    LoadingAnimation,
  },
  setup() {
    const { posts } = useBlogPosts();
    const currentPage = ref(1);
    const pageSize = 5;
    const particleContainer = ref(null);
    const isTransitioning = ref(false);
    const isLoading = ref(false);

    const totalPages = computed(() => Math.ceil(posts.value.length / pageSize));

    // 计算当前页的文章
    const paginatedPosts = computed(() => {
      const start = (currentPage.value - 1) * pageSize;
      const end = start + pageSize;
      return posts.value.slice(start, end);
    });

    // 保存当前状态
    const saveCurrentState = () => {
      const state = {
        page: currentPage.value,
        scrollY: window.scrollY,
      };
      savePageState("/", state);
    };

    // 恢复状态
    const restoreState = () => {
      const state = getPageState("/");
      if (state) {
        currentPage.value = state.page || 1;
        // 等待 DOM 更新后恢复滚动位置
        nextTick(() => {
          setTimeout(() => {
            window.scrollTo({
              top: state.scrollY || 0,
              behavior: "auto",
            });
          }, 100);
        });
      }
    };

    // 监听滚动，保存位置
    const handleScroll = () => {
      saveCurrentState();
    };

    onMounted(() => {
      restoreState();
      window.addEventListener("scroll", handleScroll, { passive: true });
    });

    onBeforeUnmount(() => {
      window.removeEventListener("scroll", handleScroll);
    });

    // 创建粒子效果
    const createParticles = () => {
      if (!particleContainer.value) return;

      const colors = ["#667eea", "#764ba2", "#f093fb", "#4facfe"];
      const particleCount = 20;

      for (let i = 0; i < particleCount; i++) {
        const particle = document.createElement("div");
        particle.className = "particle";

        const size = Math.random() * 6 + 3;
        const startX = Math.random() * 100;
        const startY = Math.random() * 100;
        const endX = startX + (Math.random() - 0.5) * 200;
        const endY = startY - Math.random() * 300;
        const duration = Math.random() * 1000 + 800;
        const delay = Math.random() * 200;
        const color = colors[Math.floor(Math.random() * colors.length)];

        particle.style.cssText = `
          position: absolute;
          width: ${size}px;
          height: ${size}px;
          background: ${color};
          border-radius: 50%;
          left: ${startX}%;
          top: ${startY}%;
          pointer-events: none;
          opacity: 0;
          box-shadow: 0 0 ${size * 2}px ${color};
        `;

        particleContainer.value.appendChild(particle);

        setTimeout(() => {
          particle.style.transition = `all ${duration}ms cubic-bezier(0.25, 0.46, 0.45, 0.94)`;
          particle.style.opacity = "1";
          particle.style.transform = `translate(${endX - startX}%, ${
            endY - startY
          }%)`;

          setTimeout(() => {
            particle.style.opacity = "0";
            setTimeout(() => particle.remove(), 300);
          }, duration * 0.7);
        }, delay);
      }
    };

    // 页面切换动画钩子
    const onBeforeEnter = (el) => {
      el.style.opacity = "0";
      el.style.transform = "translateY(30px) scale(0.95)";
    };

    const onEnter = (el, done) => {
      const delay = el.dataset.index * 100;
      setTimeout(() => {
        el.style.transition = "all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)";
        el.style.opacity = "1";
        el.style.transform = "translateY(0) scale(1)";
        done();
      }, delay);
    };

    const handlePageChange = async (page) => {
      if (isTransitioning.value) return;

      isTransitioning.value = true;
      isLoading.value = true;

      // 创建粒子效果
      createParticles();

      // 显示小行星加载动画 0.8秒
      await new Promise((resolve) => setTimeout(resolve, 800));

      currentPage.value = page;

      // 保存状态
      saveCurrentState();

      // 等待 DOM 更新
      await nextTick();

      // 平滑滚动到顶部
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });

      // 隐藏加载动画
      isLoading.value = false;

      setTimeout(() => {
        isTransitioning.value = false;
      }, 400);
    };

    return {
      posts,
      paginatedPosts,
      currentPage,
      pageSize,
      totalPages,
      particleContainer,
      isLoading,
      handlePageChange,
      onBeforeEnter,
      onEnter,
    };
  },
};
</script>

<style scoped>
/* 博客列表容器 */
.blog-list-container {
  position: relative;
  min-height: 400px;
}

/* 粒子容器 */
.particle-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 9999;
  overflow: hidden;
}

/* 卡片过渡动画 */
.blog-card-enter-active {
  transition: all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.blog-card-leave-active {
  transition: all 0.4s cubic-bezier(0.55, 0.085, 0.68, 0.53);
  position: absolute;
  width: 100%;
}

.blog-card-enter-from {
  opacity: 0;
  transform: translateY(30px) scale(0.95);
}

.blog-card-leave-to {
  opacity: 0;
  transform: translateY(-30px) scale(0.95);
}

.blog-card-move {
  transition: transform 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
}
</style>
